import { useTranslate } from "@similarweb/i18n-provider";
import { colorsPalettes } from "@similarweb/styles";
import { Badge, IconButton, OnOffSwitch, OnOffSwitchSize } from "@similarweb/ui-components";
import { Box, Flex, Text } from "@similarweb/ui-components/foundations";

import {
    ProfileAccessLevel,
    type ProfileDto,
} from "app/pages/sales-intelligence/sub-modules/contacts-search-profiles/types/dto";

type PlaybookProfileRowProps = {
    profile: ProfileDto;
    isEnabled: boolean;
    canManageShared: boolean;
    onToggle: (profileId: string, enabled: boolean) => void;
    onEdit: (profile: ProfileDto) => void;
    onDelete: (profile: ProfileDto) => void;
};

export const PlaybookProfileRow = ({
    profile,
    isEnabled,
    canManageShared,
    onToggle,
    onEdit,
    onDelete,
}: PlaybookProfileRowProps) => {
    const { translate } = useTranslate();
    const isSharedProfile = profile.accessLevel === ProfileAccessLevel.Account;
    const canManageProfile = !isSharedProfile || canManageShared;

    const handleToggle = () => {
        onToggle(profile.id, !isEnabled);
    };

    return (
        <Flex
            alignItems="center"
            justifyContent="space-between"
            borderBottom={`1px solid ${colorsPalettes.carbon["50"]}`}
            padding="12px 16px"
            gap="16px"
            data-automation={`playbook-profile-row-${profile.id}`}
        >
            <Flex
                alignItems="center"
                justifyContent="space-between"
                gap="12px"
                flex="1"
                minWidth="0"
                borderRadius="6px"
                border={`1px solid ${colorsPalettes.carbon["50"]}`}
                padding="12px 16px"
            >
                <Text
                    fontVariant="subtitle2"
                    color={colorsPalettes.carbon["500"]}
                >
                    {profile.name}
                </Text>
                <Box width="fit-content">
                    <Badge
                        bgColor={colorsPalettes.carbon["25"]}
                        color={colorsPalettes.carbon["400"]}
                        type="custom"
                    >
                        {translate(isSharedProfile ? "si.playbooks.type.shared" : "si.playbooks.type.private")}
                    </Badge>
                </Box>
            </Flex>
            <Flex
                alignItems="center"
                gap="8px"
            >
                <OnOffSwitch
                    size={OnOffSwitchSize.medium}
                    isSelected={isEnabled}
                    onClick={handleToggle}
                />
                {canManageProfile ? (
                    <>
                        <IconButton
                            type="flat"
                            iconName="edit-icon-fill"
                            iconSize="sm"
                            onClick={() => onEdit(profile)}
                            dataAutomation={`playbook-profile-edit-${profile.id}`}
                        />
                        <IconButton
                            type="flat"
                            iconName="delete"
                            iconSize="sm"
                            onClick={() => onDelete(profile)}
                            dataAutomation={`playbook-profile-delete-${profile.id}`}
                        />
                    </>
                ) : null}
            </Flex>
        </Flex>
    );
};
